import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useTranslation } from 'react-i18next'
import type { EduQuiz } from '@/components/university/education/QuizInterface'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UniMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

export interface UniEduUpdate {
  lesson_complete: boolean
  concept_understood: string | null
  weak_concept_flagged: string | null
  new_current_unit: number | null
  new_current_lesson: number | null
  trigger_quiz: boolean
  suggested_chips?: string[]
}

export interface UniSessionState {
  conversationId: string | null
  messages: UniMessage[]
  isLoading: boolean
  isStreaming: boolean
  error: string | null
  currentUnit: number
  currentLesson: number
  masteryScore: number
  sessionComplete: boolean   // lesson TRULY finished (quiz passed or no quiz required)
  suggestedChips: string[]
  lastEduUpdate: UniEduUpdate | null
  // Quiz state
  quiz: EduQuiz | null
  quizOpen: boolean
  quizLoading: boolean
  quizResult: { score: number; passed: boolean; message: string } | null
  // Completion tracking — per-lesson completion status from DB
  completedLessons: Record<string, boolean>   // key = "moduleId-lessonId"
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUniversitySession(
  classId: string,
  moduleId: number,
  lessonId: number,
  lessonTitle: string,
  lessonObjective: string,
  coreConcepts: string[],
) {
  const { user } = useAuth()
  const supabase = createClientComponentClient()
  const { i18n } = useTranslation()

  const [state, setState] = useState<UniSessionState>({
    conversationId: null,
    messages: [],
    isLoading: false,
    isStreaming: false,
    error: null,
    currentUnit: moduleId,
    currentLesson: lessonId,
    masteryScore: 0,
    sessionComplete: false,
    suggestedChips: [],
    lastEduUpdate: null,
    quiz: null,
    quizOpen: false,
    quizLoading: false,
    quizResult: null,
    completedLessons: {},
  })

  const isLoadingRef = useRef(false)
  const isStreamingRef = useRef(false)
  const streamBufferRef = useRef<string>('')
  const abortRef = useRef<AbortController | null>(null)
  const hasInitialised = useRef(false)
  const sessionStartRef = useRef<number>(Date.now())

  // ── Category ID for this class — fetched once at init, used everywhere ────
  const categoryIdRef = useRef<string | null>(null)

  // ── Overseer context — injected silently into every AI request ───────────
  const academicProfileRef = useRef<string | null>(null)

  useEffect(() => { isLoadingRef.current = state.isLoading }, [state.isLoading])
  useEffect(() => { isStreamingRef.current = state.isStreaming }, [state.isStreaming])

  // ── Helper: get Supabase URL from client (no import.meta.env needed) ─────
  const getSupabaseUrl = useCallback(() => {
    return (supabase as unknown as { supabaseUrl: string }).supabaseUrl
  }, [supabase])

  // ── Fetch global academic profile for overseer injection ─────────────────
  const fetchAcademicProfile = useCallback(async () => {
    if (!user) return
    try {
      const { data } = await supabase
        .from('users')
        .select('global_academic_profile')
        .eq('id', user.id)
        .single()
      const profile = data?.global_academic_profile as { summary?: string } | null
      if (profile?.summary) academicProfileRef.current = profile.summary
    } catch { /* non-fatal — overseer enrichment is optional */ }
  }, [user, supabase])

  // ── Write learning signals after an EDU_UPDATE ───────────────────────────
  const writeLearningSignal = useCallback(async (eduUpdate: UniEduUpdate) => {
    if (!user || !classId) return
    const duration = Math.round((Date.now() - sessionStartRef.current) / 1000)
    try {
      await supabase.from('learning_signals').upsert({
        user_id: user.id,
        class_id: classId,
        module_id: moduleId,
        lesson_id: lessonId,
        // session_duration_seconds: server-authored by quiz-engine (lesson_started_at clock) — client no longer reports time
        lesson_completed: eduUpdate.lesson_complete,
        concepts_understood: eduUpdate.concept_understood ? [eduUpdate.concept_understood] : [],
        concepts_struggled: eduUpdate.weak_concept_flagged ? [eduUpdate.weak_concept_flagged] : [],
        sage_source: 'university',
      }, { onConflict: 'user_id,class_id,module_id,lesson_id' })

      // If the lesson is complete, fire the Academic Overseer in the background
      if (eduUpdate.lesson_complete) {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        fetch(`${getSupabaseUrl()}/functions/v1/ai-academic-overseer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            user_id: user.id,
            trigger: 'module_complete',
            trigger_slug: classId,
          }),
        }).catch(() => { /* silent — overseer failure never affects the session */ })
      }
    } catch { /* non-fatal — signal write failure never disrupts teaching */ }
  }, [user, classId, moduleId, lessonId, supabase, getSupabaseUrl])

  // ── Fetch lesson completion status from DB for sidebar ticks ─────────────
  const fetchCompletedLessons = useCallback(async () => {
    if (!user || !classId) return
    try {
      const { data: enrol } = await supabase
        .from('class_enrollments')
        .select('progress_state')
        .eq('student_id', user.id)
        .eq('class_id', classId)
        .maybeSingle()

      if (enrol?.progress_state) {
        const ps = enrol.progress_state as {
          current_unit?: number
          current_lesson?: number
          total_lessons_completed?: number
          completed_lessons?: Record<string, boolean>
        }
        if (ps.completed_lessons) {
          setState(s => ({ ...s, completedLessons: ps.completed_lessons! }))
        } else if (ps.current_unit && ps.current_lesson) {
          // Build from current_unit/current_lesson for backwards compat
          const completed: Record<string, boolean> = {}
          // All lessons in earlier modules are done
          for (let m = 1; m < ps.current_unit; m++) {
            for (let l = 1; l <= 20; l++) completed[`${m}-${l}`] = true
          }
          // Lessons before current in the current module
          for (let l = 1; l < ps.current_lesson; l++) {
            completed[`${ps.current_unit}-${l}`] = true
          }
          setState(s => ({ ...s, completedLessons: completed }))
        }
      }
    } catch { /* non-fatal */ }
  }, [user, classId, supabase])

  // ── Update class_enrollments.progress_state after quiz pass ──────────────
  const updateEnrollmentProgress = useCallback(async (newUnit: number, newLesson: number) => {
    if (!user || !classId) return
    try {
      // First read existing progress_state
      const { data: enrol } = await supabase
        .from('class_enrollments')
        .select('progress_state')
        .eq('student_id', user.id)
        .eq('class_id', classId)
        .maybeSingle()

      const existing = (enrol?.progress_state || {}) as Record<string, unknown>
      const completedLessons = (existing.completed_lessons || {}) as Record<string, boolean>

      // Mark this lesson as done
      completedLessons[`${moduleId}-${lessonId}`] = true

      // Count total lessons completed
      const totalDone = Object.values(completedLessons).filter(Boolean).length

      const newState = {
        ...existing,
        current_unit: newUnit,
        current_lesson: newLesson,
        total_lessons_completed: totalDone,
        completed_lessons: completedLessons,
        last_completed_at: new Date().toISOString(),
      }

      await supabase
        .from('class_enrollments')
        .update({
          progress_state: newState,
          last_active_at: new Date().toISOString(),
        })
        .eq('student_id', user.id)
        .eq('class_id', classId)

      // Update local state too
      setState(s => ({
        ...s,
        completedLessons: { ...completedLessons },
        currentUnit: newUnit,
        currentLesson: newLesson,
      }))

      // Force a refetch of completed lessons to ensure UI is perfectly in sync with DB
      setTimeout(() => fetchCompletedLessons(), 800)
    } catch (err) {
      console.error('updateEnrollmentProgress error:', err)
    }
  }, [user, classId, moduleId, lessonId, supabase])

  // ── Initialise: get or create conversation for this class+module+lesson ──
  const initialise = useCallback(async () => {
    if (!user || !classId || hasInitialised.current) return
    hasInitialised.current = true
    sessionStartRef.current = Date.now()

    setState(s => ({
      ...s,
      isLoading: true,
      error: null,
      sessionComplete: false,
      quiz: null,
      quizOpen: false,
      quizResult: null,
      suggestedChips: [],
    }))

    // Fetch academic profile + completion status in parallel (non-blocking)
    fetchAcademicProfile()
    fetchCompletedLessons()

    try {
      const supabaseClient = supabase

      // ── Step 1: Resolve category_id for this class ──────────────────────
      const { data: classRow, error: classErr } = await supabaseClient
        .from('classes')
        .select('category_id')
        .eq('id', classId)
        .single()

      if (classErr || !classRow?.category_id) {
        throw new Error('Could not find a category for this class. Please contact support.')
      }
      categoryIdRef.current = classRow.category_id

      // ── Step 2: Look for existing conversation for this specific lesson ─
      const { data: existing } = await supabaseClient
        .from('conversations')
        .select('id')
        .eq('user_id', user.id)
        .contains('metadata', {
          session_type: 'university',
          class_id: classId,
          module_id: moduleId,
          lesson_id: lessonId,
        })
        .order('created_at', { ascending: false })
        .limit(1)

      let convId: string

      if (existing && existing.length > 0) {
        convId = existing[0].id
      } else {
        // ── Step 3: Create new conversation with the resolved category_id ─
        const { data: newConv, error: createErr } = await supabaseClient
          .from('conversations')
          .insert({
            user_id: user.id,
            category_id: categoryIdRef.current,   // ← FIX: never null
            title: lessonTitle,
            metadata: {
              session_type: 'university',
              class_id: classId,
              module_id: moduleId,
              lesson_id: lessonId,
              lesson_title: lessonTitle,
            },
          })
          .select('id')
          .single()

        if (createErr) throw createErr
        convId = newConv.id
      }

      // Load message history for this lesson
      const { data: msgs } = await supabaseClient
        .from('messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true })
        .limit(100)

      // Load enrolment progress
      const { data: enrol } = await supabaseClient
        .from('class_enrollments')
        .select('mastery_score, progress_state')
        .eq('student_id', user.id)
        .eq('class_id', classId)
        .maybeSingle()

      const mastery = Number(enrol?.mastery_score) || 0

      setState(s => ({
        ...s,
        conversationId: convId,
        messages: (msgs || []) as UniMessage[],
        isLoading: false,
        masteryScore: mastery,
      }))
    } catch (err) {
      setState(s => ({
        ...s,
        isLoading: false,
        error: (err as Error).message || 'Failed to start session',
      }))
    }
  }, [user, classId, moduleId, lessonId, lessonTitle, supabase, fetchAcademicProfile, fetchCompletedLessons])

  useEffect(() => { initialise() }, [initialise])

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    if (!user || !state.conversationId) return
    if (isLoadingRef.current || isStreamingRef.current) return
    if (!text.trim()) return

    const userMsg: UniMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      created_at: new Date().toISOString(),
    }

    setState(s => ({
      ...s,
      messages: [...s.messages, userMsg],
      isLoading: true,
      error: null,
      suggestedChips: [],  // clear chips when user sends a message
    }))

    abortRef.current = new AbortController()
    streamBufferRef.current = ''

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const supabaseUrl = getSupabaseUrl()

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          conversation_id: state.conversationId,
          message: text.trim(),
          category_id: categoryIdRef.current,
          university_mode: true,
          class_id: classId,
          current_module: moduleId,
          current_lesson: lessonId,
          lesson_title: lessonTitle,
          lesson_objective: lessonObjective,
          core_concepts: coreConcepts,
          session_type: 'university',
          language: i18n.language || 'en',
          academic_profile: academicProfileRef.current ?? null,
        }),
        signal: abortRef.current.signal,
      })

      if (!response.ok) throw new Error(`AI service error: ${response.status}`)

      const contentType = response.headers.get('content-type') || ''

      // Non-streaming fallback
      if (contentType.includes('application/json')) {
        const json = await response.json()
        const assistantMsg: UniMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: json.message || json.content || '',
          created_at: new Date().toISOString(),
        }
        setState(s => ({ ...s, isLoading: false, messages: [...s.messages, assistantMsg] }))
        return
      }

      // ── Streaming SSE ─────────────────────────────────────────────────────
      setState(s => ({ ...s, isLoading: false, isStreaming: true }))

      const assistantMsgId = `stream-${Date.now()}`
      setState(s => ({
        ...s,
        messages: [...s.messages, {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
        }],
      }))

      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let sseBuffer = ''
      let receivedDone = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const combined = sseBuffer + decoder.decode(value, { stream: true })
        const lines = combined.split('\n')
        sseBuffer = lines.pop() || ''   // hold the possibly-incomplete last line

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data: ')) continue
          const data = trimmed.slice(6)
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)

            if (parsed.content) {
              streamBufferRef.current += parsed.content

              let displayContent = streamBufferRef.current
                .split('EDU_UPDATE')[0]
                .replace(/EDU_UPDATE::\{[^}]*\}/g, '')
                .replace(/EDU_UPDATE::/g, '')
                .replace(/EDU_UPDATE/g, '')
                .replace(/\bEDU\s*$/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trimEnd()

              setState(s => ({
                ...s,
                messages: s.messages.map(m =>
                  m.id === assistantMsgId ? { ...m, content: displayContent } : m
                ),
              }))
            }

            if (parsed.done) {
              receivedDone = true
              const eduUpdate = parsed.eduUpdate as UniEduUpdate | undefined

              const lessonDone = eduUpdate?.lesson_complete ?? false
              const quizTriggered = eduUpdate?.trigger_quiz ?? false

              setState(s => ({
                ...s,
                isStreaming: false,
                lastEduUpdate: eduUpdate ?? s.lastEduUpdate,
                sessionComplete: lessonDone && !quizTriggered && !s.quizLoading,
                suggestedChips: eduUpdate?.suggested_chips || s.suggestedChips,
              }))

              if (eduUpdate) {
                writeLearningSignal(eduUpdate)
              }

              if (quizTriggered) {
                fetchQuiz()
              }

              if (lessonDone && !quizTriggered) {
                // INTERIM (2026-10-02): no client write to class_enrollments here.
                // The removed call — updateEnrollmentProgress(moduleId, lessonId + 1) —
                // wrote hierarchical IDs into fields the server keeps SEQUENTIAL, with
                // no module-boundary handling. scoreQuiz owns this write on quiz
                // completions; the no-quiz writer is an engine-source decision.
                void (async () => {
                  try {
                    const { data: enrol } = await supabase
                      .from('class_enrollments')
                      .select('mastery_score')
                      .eq('student_id', user.id)
                      .eq('class_id', classId)
                      .maybeSingle()
                    if (enrol) {
                      setState(s => ({
                        ...s,
                        masteryScore: Number(enrol.mastery_score) || s.masteryScore,
                      }))
                    }
                  } catch { /* non-fatal */ }
                })()
              }
            }
          } catch { /* skip malformed chunks */ }
        }
      }

      // Safety net: if the done event was lost at a chunk boundary, unlock the input
      if (!receivedDone) {
        setState(s => ({ ...s, isStreaming: false }))
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      setState(s => ({
        ...s,
        isLoading: false,
        isStreaming: false,
        error: (err as Error).message || 'Failed to send message',
      }))
    }
  }, [user, state.conversationId, classId, moduleId, lessonId, lessonTitle, lessonObjective, coreConcepts, supabase, i18n.language, getSupabaseUrl, writeLearningSignal, updateEnrollmentProgress])

  const clearError = useCallback(() => {
    setState(s => ({ ...s, error: null }))
  }, [])

  const reinitialise = useCallback(() => {
    hasInitialised.current = false
    initialise()
  }, [initialise])

  // ── Fetch quiz from edge function ─────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchQuiz = useCallback(async () => {
    if (!user || !state.conversationId) return
    setState(s => ({ ...s, quizLoading: true }))

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const supabaseUrl = getSupabaseUrl()

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          conversation_id: state.conversationId,
          message: '[quiz_request]',
          category_id: categoryIdRef.current,
          university_mode: true,
          class_id: classId,
          current_module: moduleId,
          current_lesson: lessonId,
          language: i18n.language || 'en',
        }),
      })

      if (!response.ok) throw new Error('Quiz generation failed')
      const data = await response.json()

      if (data.quiz) {
        setState(s => ({
          ...s,
          quiz: data.quiz as EduQuiz,
          quizOpen: true,
          quizLoading: false,
          sessionComplete: false, // Ensure button is hidden while quiz is open
        }))
      } else {
        // No quiz returned — lesson complete without quiz
        setState(s => ({ ...s, quizLoading: false, sessionComplete: true }))
         // No progress write — the caller advances via goNextLesson() which knows the plan
      }
    } catch (err) {
      console.error('fetchQuiz error:', err)
      setState(s => ({ ...s, quizLoading: false, error: 'Failed to load quiz' }))
    }
  }, [user, state.conversationId, classId, moduleId, lessonId, supabase, i18n.language, getSupabaseUrl, updateEnrollmentProgress])

  // ── Submit quiz answers to edge function ──────────────────────────────────
  const submitQuiz = useCallback(async (answers: Record<string, number>) => {
    if (!user || !state.conversationId || !state.quiz) return

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const supabaseUrl = getSupabaseUrl()

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-education`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          conversation_id: state.conversationId,
          message: '[quiz_submission]',
          quiz_answers: answers,
          category_id: categoryIdRef.current,
          university_mode: true,
          class_id: classId,
          current_module: moduleId,
          current_lesson: lessonId,
          language: i18n.language || 'en',
        }),
      })

      if (!response.ok) throw new Error('Quiz scoring failed')
      const result = await response.json()

      const quizScore = result.score ?? result.quiz_result?.score ?? 0
      const quizPassed = result.passed ?? result.quiz_result?.passed ?? false
      const quizMessage = result.message ?? result.quiz_result?.message ?? ''

      // Store quiz result for UI display
      setState(s => ({
        ...s,
        quizResult: { score: quizScore, passed: quizPassed, message: quizMessage },
        quizOpen: false,
      }))

      if (quizPassed) {
        // Optimistically update the lesson as complete in the UI immediately
        const lessonKey = `${moduleId}-${lessonId}`
        setState(s => ({
          ...s,
          sessionComplete: true,
          quiz: null,
          completedLessons: {
            ...s.completedLessons,
            [lessonKey]: true
          }
        }))

        // Server already wrote class_enrollments.progress_state in scoreQuiz step 8
// (with correct module-boundary handling). Do NOT write it again from the
// client — we don't know the plan shape here and will clobber the correct value.

        // Refresh mastery
        void (async () => {
          try {
            const { data: enrol } = await supabase
              .from('class_enrollments')
              .select('mastery_score')
              .eq('student_id', user.id)
              .eq('class_id', classId)
              .maybeSingle()
            if (enrol) {
              setState(s => ({
                ...s,
                masteryScore: Number(enrol.mastery_score) || s.masteryScore,
              }))
            }
          } catch { /* non-fatal */ }
        })()
      } else {
        // ── FAILED: Send review request to AI for weak concepts ──────────
        const weakConcepts = (result.details || result.quiz_result?.details || [])
          .filter((d: { correct: boolean }) => !d.correct)
          .map((d: { concept: string }) => d.concept)
          .filter(Boolean)

        // Post a review message automatically
        const reviewPrompt = weakConcepts.length > 0
          ? `[quiz_review] I scored ${quizScore}%. Please give me a brief review lesson on these concepts I got wrong: ${weakConcepts.join(', ')}. Then I'll retake the quiz.`
          : `[quiz_review] I scored ${quizScore}%. Please review the material I struggled with and help me prepare for a retake.`

        // Add quiz result as a system message visible to user
        const quizResultMsg: UniMessage = {
          id: `quiz-result-${Date.now()}`,
          role: 'assistant',
          content: `📊 **Quiz Result: ${quizScore}%** — You need 70% to pass.\n\n${quizMessage}\n\n${weakConcepts.length > 0 ? `**Areas to review:** ${weakConcepts.join(', ')}\n\n` : ''}Let me help you review what you missed…`,
          created_at: new Date().toISOString(),
        }

        setState(s => ({
          ...s,
          quiz: null,
          messages: [...s.messages, quizResultMsg],
          suggestedChips: ['Review weak areas', 'Retake quiz', 'Ask a question'],
        }))

        // Automatically request a review lesson from the AI
        setTimeout(() => {
          sendMessage(reviewPrompt)
        }, 1500)
      }

      return result
    } catch (err) {
      console.error('submitQuiz error:', err)
      setState(s => ({ ...s, error: 'Failed to submit quiz. Please try again.' }))
      throw err
    }
  }, [user, state.conversationId, state.quiz, classId, moduleId, lessonId, supabase, i18n.language, getSupabaseUrl, updateEnrollmentProgress, sendMessage])

  // ── Dismiss quiz modal ────────────────────────────────────────────────────
  const dismissQuiz = useCallback(() => {
    setState(s => ({ ...s, quizOpen: false }))
  }, [])

  return {
    ...state,
    sendMessage,
    clearError,
    reinitialise,
    fetchQuiz,
    submitQuiz,
    dismissQuiz,
    updateEnrollmentProgress,
  }
}
